import {
  ActivityEvent,
  ActivityEventTypeId,
  DayGroup,
  EventTypeGroup,
  TimeWindowValue,
} from '../../types';

// Alert/Comment/Notification rows open the
const CLICKABLE_EVENT_TYPE_IDS = new Set<ActivityEventTypeId>([
  'alert_viewed',
  'alert_acknowledged',
  'alert_resolved',
  'alert_dismissed',
  'comment_viewed',
  'comment_added',
  'notification_viewed',
  'notification_added_edited',
]);

/** An execution-leg view has no shipment_id; it opens by its leg id instead. */
export function isExecutionLegEvent(event: ActivityEvent): boolean {
  return (
    event?.leg_type === 'execution_leg' && Boolean(event?.execution_leg_id)
  );
}

/** Company-wide alerts aren't tied to a shipment; they open the Settings →
 * Notifications edit drawer using the alert id carried in `coc_id`. */
export function isCompanyAlertNotificationEvent(event: ActivityEvent): boolean {
  return (
    CLICKABLE_EVENT_TYPE_IDS.has(event.typeId) &&
    event.notification_source === 'company_alert' &&
    Boolean(event.coc_id)
  );
}

// Sources whose rows live in a shipment's own notification list: a regular
// per-shipment notification, or a geo-fence (its row id is the fence id).
const SHIPMENT_NOTIFICATION_SOURCES = new Set([
  'shipment_notification',
  'geo_fence',
]);

/** A shipment-specific notification add/edit or view: opens that shipment's
 * notification drawer with the notification (id in `coc_id`) highlighted. */
export function isShipmentNotificationEvent(event: ActivityEvent): boolean {
  if (!SHIPMENT_NOTIFICATION_SOURCES.has(event.notification_source ?? '')) {
    return false;
  }
  // A drawer-open view has no single notification id, it still opens the drawer.
  if (event.typeId === 'notification_viewed') return Boolean(event.shipment_id);
  return event.typeId === 'notification_added_edited' && Boolean(event.coc_id);
}

export function isClickableEvent(event: ActivityEvent): boolean {
  if (isCompanyAlertNotificationEvent(event)) return true;
  return (
    (CLICKABLE_EVENT_TYPE_IDS.has(event.typeId) &&
      Boolean(event.shipment_id)) ||
    isExecutionLegEvent(event)
  );
}

/** Groups by each event's own `date` label — already formatted by the
 * backend, no timezone math needed here. Assumes `events` is sorted
 * newest-first, so groups come out newest-day-first too. */
export function groupByDay(events: ActivityEvent[]): DayGroup[] {
  const map = new Map<string, DayGroup>();
  for (const event of events) {
    let group = map.get(event.date);
    if (!group) {
      group = { key: event.date, label: event.date, events: [] };
      map.set(event.date, group);
    }
    group.events.push(event);
  }
  return Array.from(map.values());
}

export function getAllLeafEventTypeIds(groups: EventTypeGroup[]): string[] {
  return groups.flatMap((g) =>
    g.children ? g.children.map((c) => c.id) : [g.id]
  );
}

export function usersChipLabel(
  selectedCount: number,
  totalCount: number
): string {
  if (selectedCount === totalCount) return 'All users';
  if (selectedCount === 1) return '1 user';
  return `${selectedCount} users`;
}

export function eventsChipLabel(
  selectedCount: number,
  totalCount: number
): string {
  if (selectedCount === totalCount) return 'All events';
  if (selectedCount === 1) return '1 event';
  return `${selectedCount} events`;
}

export function timeWindowChipLabel(window: TimeWindowValue): string {
  switch (window.preset) {
    case '24h':
      return 'Last 24 hours';
    case '30d':
      return 'Last 30 days';
    case 'custom':
      return 'Custom';
    case '7d':
    default:
      return 'Last 7 days';
  }
}
